import db from "@/db";
import { recordKey } from "@/db/pendingRecord";
import { isRejection, isTransientFailure } from "@/utils/network";
import { extractErrorDetail } from "@/utils/errorDetail";
import { serializeBody } from "@/setup/sentryAxiosResponse";

const isWaiting = (row) => !row.rejected && !row.failed;

export const pendingProjects = () =>
  db.pendingSyncs.orderBy("projectUuid").uniqueKeys();

export const isProjectPending = async (projectUuid) =>
  (await db.pendingSyncs.where({ projectUuid }).filter(isWaiting).count()) > 0;

export const trackedRows = async (table, uuids) =>
  (
    await db.pendingSyncs.bulkGet(uuids.map((uuid) => recordKey(table, uuid)))
  ).filter(Boolean);

export const pendingRecords = (projectUuid) =>
  db.pendingSyncs.where({ projectUuid }).toArray();

export const markRecordsPending = async (
  projectUuid,
  table,
  uuids,
  page,
  inOfflineMode = false
) => {
  const kept = uuids.filter(Boolean);
  const skeletons = new Set(
    (await trackedRows(table, kept))
      .filter(({ skeleton }) => skeleton)
      .map(({ uuid }) => uuid)
  );
  return db.pendingSyncs.bulkPut(
    kept.map((uuid) => ({
      key: recordKey(table, uuid),
      projectUuid,
      table,
      uuid,
      page,
      at: Date.now(),
      skeleton: skeletons.has(uuid),
      inOfflineMode,
    }))
  );
};

export const restoreTracked = async (table, uuids, previous) => {
  const kept = new Set(previous.map(({ key }) => key));
  await db.pendingSyncs.bulkDelete(
    uuids.map((uuid) => recordKey(table, uuid)).filter((key) => !kept.has(key))
  );
  await db.pendingSyncs.bulkPut(previous);
};

export const markSkeleton = (table, uuid) =>
  db.pendingSyncs.update(recordKey(table, uuid), { skeleton: true });

const syncProblem = (error) => ({
  status: error?.response?.status ?? null,
  detail: extractErrorDetail(error),
  response: serializeBody(error?.response?.data) ?? null,
  at: Date.now(),
});

const reportSyncProblem = async (kind, rows, error) => {
  if (!rows.length) return;
  const { queueSyncProblem } = await import("@/composables/network/syncReport");
  queueSyncProblem({ kind, projectUuid: rows[0].projectUuid, rows, error });
};

export const rejectTracked = async (table, uuids, error) => {
  if (!isRejection(error)) return false;
  const rows = await trackedRows(table, uuids);
  const rejected = syncProblem(error);
  await Promise.all(
    rows.map(({ key }) =>
      db.pendingSyncs.update(key, { rejected, failed: null })
    )
  );
  await reportSyncProblem(
    "rejected",
    rows.filter((row) => !row.rejected),
    error
  );
  return rows.length > 0;
};

const attributedErrors = new WeakSet();

export const isAttributedFailure = (error) =>
  error instanceof Object && attributedErrors.has(error);

export const failTracked = async (table, uuids, error) => {
  if (isTransientFailure(error)) return;
  const rows = await trackedRows(table, uuids);
  if (rows.length && error instanceof Object) attributedErrors.add(error);
  const fresh = rows.filter(({ failed }) => !failed);
  const failed = syncProblem(error);
  await Promise.all(
    fresh.map(({ key }) =>
      db.pendingSyncs.update(key, { failed, rejected: null })
    )
  );
  await reportSyncProblem("failed", fresh, error);
};

export const unmarkRecords = (keys) => db.pendingSyncs.bulkDelete(keys);
